import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import config from '../config';

interface BetaLabsContextType {
  isBetaLabsEnabled: boolean;
  toggleBetaLabs: () => void;
  isFeatureEnabled: (featureKey: string) => boolean;
  isBetaLabsAvailable: boolean; // indicates if Beta Labs is available in this environment
}

const BetaLabsContext = createContext<BetaLabsContextType | undefined>(undefined);

const BETA_LABS_STORAGE_KEY = 'api-hub-beta-labs-enabled';

// Define beta features that should be gated
export const BETA_FEATURES = {
  INTEGRATION_MAPPER: 'integration-mapper',
  INTEGRATION_ECOSYSTEM: 'integration-ecosystem',
} as const;

interface BetaLabsProviderProps {
  children: ReactNode;
}

export const BetaLabsProvider: React.FC<BetaLabsProviderProps> = ({ children }) => {
  const [isBetaLabsEnabled, setIsBetaLabsEnabled] = useState<boolean>(false);
  
  // Check if Beta Labs is available in this environment
  const isBetaLabsAvailable = config.features.betaLabsEnabled;

  // Load beta labs preference from localStorage on mount
  useEffect(() => {
    const storedPreference = localStorage.getItem(BETA_LABS_STORAGE_KEY);
    if (storedPreference) {
      setIsBetaLabsEnabled(JSON.parse(storedPreference));
    }
  }, []);

  // Save beta labs preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(BETA_LABS_STORAGE_KEY, JSON.stringify(isBetaLabsEnabled));
  }, [isBetaLabsEnabled]);

  const toggleBetaLabs = () => {
    setIsBetaLabsEnabled(prev => !prev);
  };

  const isFeatureEnabled = (featureKey: string): boolean => {
    // Only allow features if Beta Labs is available in environment AND user has opted in
    return isBetaLabsAvailable && isBetaLabsEnabled;
  };

  const value: BetaLabsContextType = {
    isBetaLabsEnabled,
    toggleBetaLabs,
    isFeatureEnabled,
    isBetaLabsAvailable,
  };

  return (
    <BetaLabsContext.Provider value={value}>
      {children}
    </BetaLabsContext.Provider>
  );
};

export const useBetaLabs = (): BetaLabsContextType => {
  const context = useContext(BetaLabsContext);
  if (context === undefined) {
    throw new Error('useBetaLabs must be used within a BetaLabsProvider');
  }
  return context;
};
